import { Component } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { AuthService } from '../service/auth.service';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { LanguageService } from '../service/language.service';
import * as dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/pt';
import { UserService } from '../service/user.service';
import { LocalCache } from '../enum/local.cache';
import { DICT_THEME } from '../model/profile';
import { changeContentMetaThemeColor } from '../utils/utils';
import { PlatformService } from '../service/platform.service';
import { bindDeviceClassToRoot, bindOsClassToRoot } from '../lib/device';

@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss']
})
export class BaseComponent {
  constructor(
    public translate: TranslateService,
    private authService: AuthService,
    private matIconRegistry: MatIconRegistry,
    private languageService: LanguageService,
    private dateAdapter: DateAdapter<Date>,
    private userService: UserService,
    private platform: PlatformService
  ) {
    // Sinaliza no <body> que o app está rodando como PWA instalada (e
    // não como aba normal de navegador). Permite SCSS condicional como
    // `body.installed-app .navbar { border-top: ... }` para compensar
    // a ausência do chrome do browser.
    if (this.platform.isInstalledApp) {
      document.body.classList.add('installed-app');
    }


    // Aplica classes de dispositivo no <html> — habilita SCSS hooks
    // tipo `html[data-device="mobile-portrait"] .foo { ... }`. Reativo
    // a mudanças de orientação/pointer via listeners internos. Chamada
    // ÚNICA no bootstrap; sem cleanup porque o app dura a sessão.
    bindDeviceClassToRoot();
    // Detecta SO (mac/windows/linux/other) e carimba `data-os` no <html>.
    // Usado pelo popover pra respeitar a convenção nativa de cada plataforma
    // (macOS instantâneo; Windows/Linux com animação sutil).
    bindOsClassToRoot();
    // `bindBrowserClassToRoot()` (data-browser="safari" no <html>) foi
    // retirado no macOS 27 — o Safari agora desenha a separação
    // viewport↔canvas nativamente e o reforço virava linha dupla.
    // Helper permanece em `lib/device.ts` pra reativação rápida se
    // aparecer novo quirk específico de browser.

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      localStorage.setItem('language', event.lang);
      this.dateAdapter.setLocale(event.lang);
      dayjs.locale(event.lang.toLowerCase());
      // a11y (WCAG 3.1.2): sincroniza o `<html lang>` com o idioma
      // ativo. Sem isso, screen readers leem todo o conteúdo PT-BR com
      // pronúncia inglesa (e vice-versa), porque `index.html` traz
      // `<html lang="en">` estático.
      document.documentElement.lang = event.lang;
    });

    translate.setDefaultLang('en');

    const language = localStorage.getItem('language');

    if (language) {
      translate.use(language);
    } else {
      const lang = translate.getBrowserCultureLang();

      if (this.languageService.translationExists(lang)) {
        translate.use(lang);
      }
    }

    this.setupTokenRefreshMocker();
    this.setTheme();
  }

  private setupTokenRefreshMocker() {
    setInterval(() => {
      const refresh = localStorage.getItem('refresh');
      if (refresh && this.authService.isAuthenticated()) {
        this.authService.refreshToken({ refresh }).subscribe(
          response => localStorage.setItem('token', response.data.access),
          // Best-effort: falha (401 incluso) não pode deslogar nem recarregar a
          // página em segundo plano; mantém o token atual até a próxima tentativa.
          () => { }
        );
      }
    }, 1000 * 60 * 5);
  }

  private setTheme(): void {
    let theme = LocalCache.getValue(LocalCache.OLD_THEME) as string;
    if (!theme || theme === DICT_THEME.SYSTEM) {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
          theme = DICT_THEME.DARK;
        } else {
          theme = DICT_THEME.LIGHT;
      }
    }
    changeContentMetaThemeColor(theme);
    document.body.classList.add(theme);
  }
}
